"use client"

// Stateless presentational panel used by both popover and dialog variants
// of the family picker container. Families are read-only here; new families
// are defined on the Product Families page (?page=shopify-bundle-rules).
//
// Receives the LIGHT shape (ProductFamilyLight) — only id/name/family_key/
// product_count. Per-product hydration happens in the parent on selection;
// this panel never reads `family.products`. The container closes the
// picker immediately on click, so there is no in-panel pending state —
// the «adding family…» indicator lives in the parent's items list area.

import { Loader2, Maximize2, Settings } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { ProductFamilyLight } from "@/types/shopify"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


export interface SetPickerPanelProps {
  families: ProductFamilyLight[]
  isLoading: boolean
  /** Family IDs already in the list (for duplicate prevention) */
  excludeFamilyIds: Set<string>
  onSelect: (family: ProductFamilyLight) => void

  /** Dense = dialog variant (2-col grid, taller list). */
  dense?: boolean
  /** When provided, renders a Maximize2 button that calls this callback. */
  onRequestExpand?: () => void
}

export function SetPickerPanel({
  families,
  isLoading,
  excludeFamilyIds,
  onSelect,
  dense = false,
  onRequestExpand,
}: SetPickerPanelProps) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsSetPickerPanel")
  const tCommonUi = useTranslations("Common")

  return (
    <div className={cn("flex flex-col min-h-0", dense && "h-full")}>
      <div className="px-3 py-2 border-b flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-medium"><span key={tUi("productFamilies")}>{tUi("productFamilies")}</span></div>
          <a
            href="?page=shopify-bundle-rules"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <Settings className="h-3 w-3" />
            <span><span key={tUi("openProductFamilies")}>{tUi("openProductFamilies")}</span></span>
          </a>
        </div>
        {onRequestExpand && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 shrink-0"
            onClick={onRequestExpand}
            aria-label={tUi("expandToFullScreen")}
            title={tUi("expandToFullScreen")}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div
        className={cn(
          "overflow-y-auto",
          dense ? "flex-1 min-h-0" : "max-h-80",
        )}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            <span><span key={tCommonUi("loading")}>{tCommonUi("loading")}</span></span>
          </div>
        )}
        {!isLoading && families.length === 0 && (
          <a
            href="?page=shopify-bundle-rules"
            className="block py-4 text-center text-sm text-muted-foreground hover:bg-muted/50"
          >
            <span key={tUi("noFamiliesYetOpenProductFamiliesToDefine")}>{tUi("noFamiliesYetOpenProductFamiliesToDefine")}</span></a>
        )}
        <div className={cn(dense && "grid grid-cols-1 md:grid-cols-2 gap-2 p-2")}>
          {families.map((family) => {
            const isDuplicate = excludeFamilyIds.has(family.id)
            return (
              <button
                key={family.id}
                type="button"
                disabled={isDuplicate}
                onClick={() => onSelect(family)}
                className={cn(
                  dense
                    ? "text-left rounded-md border p-3 transition-colors hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    : "w-full text-left px-3 py-2 hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed border-b last:border-b-0",
                )}
              >
                <div className="text-sm font-medium truncate">{family.name}</div>
                <div className="text-xs text-muted-foreground">
                  {family.product_count} <span><span key={tUi("products")}>{tUi("products")}</span></span>
                  <span>{isDuplicate && " — already added"}</span>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
